import { settings } from '@/_config';
import type {
  TCheckoutAPIDataModel,
  TCollegeModelData,
  TCouponModelData,
  TGlobalDataModel,
  TLeadModelData,
  TMetaDataModelData,
} from '@/_models';
import bridgePlanApis from '@/_state/apis/bridge-plan';
import checkoutApis from '@/_state/apis/checkout-apis';

export type ErrorData = {
  success: boolean;
  message: string;
  error_code: string;
};

// api.achievetestprep.com
const extendedApi = bridgePlanApis.injectEndpoints({
  endpoints: (builder) => ({
    getLead: builder.query<
      TLeadModelData,
      { lead_id: string; cpId: string; campusId: string; tcValue: string | undefined }
    >({
      query: ({ lead_id, cpId, campusId, tcValue }) =>
        `leads/${lead_id}/?cp_id=${cpId}&campus_id=${campusId}&tc=${tcValue}`,
      providesTags: ['LEAD_BP_PLAN'],
    }),

    getCollegeInfo: builder.query<TCollegeModelData, { college_id: string; leadId: string }>({
      query: ({ college_id, leadId }) => `campus-to-program/${college_id}/?lead=${leadId}`,
    }),

    postLeadData: builder.mutation<
      TMetaDataModelData,
      {
        leadId: string;
        summaryId: string;
        campusId: string;
        leadData: TGlobalDataModel;
      }
    >({
      query: ({ leadId, summaryId, leadData, campusId }) => ({
        url: 'lead-course-order/',
        method: 'POST',
        body: { lead_id: leadId, summary_id: summaryId, campus_id: campusId, order_data: leadData },
      }),
    }),

    submitLead: builder.mutation<
      string,
      {
        leadId: string;
        body: TCheckoutAPIDataModel;
      }
    >({
      query: ({ leadId, body }) => ({
        url: `lead/${leadId}/submit-data/`,
        method: 'PATCH',
        body,
      }),
    }),

    customerLink: builder.mutation<{ key: string }, { leadId: string; cpId: string; campusId: string }>({
      query: ({ leadId, cpId, campusId }) => `get-lead-link/${leadId}/?cp_id=${cpId}&campus_id=${campusId}`,
      invalidatesTags: ['LEAD_BP_PLAN'],
    }),

    validateCustomerLink: builder.query<
      { expired: string },
      { leadId: string; cpId: string; campusId: string; key: string }
    >({
      query: ({ leadId, cpId, campusId, key }) =>
        `validate-lead-link/${leadId}/?cp_id=${cpId}&campus_id=${campusId}&key=${key}`,
    }),

    orderData: builder.query<TGlobalDataModel, { leadId: string; cpId: string; campusId: string }>({
      query: ({ leadId, cpId, campusId }) => `order-data/${leadId}/?cp_id=${cpId}&campus_id=${campusId}`,
    }),

    bpInvoiceLink: builder.query<{ data: { invoice_pdf_link: string } }, { leadId: string }>({
      query: ({ leadId }) => `lead/bridge-plan/${leadId}/`,
    }),

    declineTermsConditions: builder.mutation<
      TMetaDataModelData,
      {
        leadId: string;
      }
    >({
      query: ({ leadId }) => ({
        url: `decline/tc/${leadId}/`,
        method: 'POST',
        body: {},
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetLeadQuery,
  useGetCollegeInfoQuery,
  usePostLeadDataMutation,
  useSubmitLeadMutation,
  useCustomerLinkMutation,
  useValidateCustomerLinkQuery,
  useOrderDataQuery,
  useBpInvoiceLinkQuery,
  useDeclineTermsConditionsMutation,
} = extendedApi;

// api.checkout
const extendedCheckApis = checkoutApis.injectEndpoints({
  endpoints: (builder) => ({
    getMetadata: builder.query<
      TMetaDataModelData,
      {
        total_selected_prep_courses: number;
        total_selected_odt_courses: number;
        total_selected_fundamental_courses: number;
        total_selected_courses: number;
        leadId: string;
      }
    >({
      query: ({ leadId, ...rest }) => ({
        url: `order-metadata/${leadId}`,
        method: 'POST',
        body: {
          ...rest,
          subscription_type: 'monthly,bi-weekly',
        },
      }),

      transformResponse: (data: TMetaDataModelData, _meta, arg) => {
        if (arg.total_selected_courses < settings.bridgePlan.minCourseToMatchup) {
          const pt = data.payment_terms.map((p) => ({ ...p, discount_rate: 0 }));

          // return data;
          return { ...data, discount_percentage: 0, payment_terms: [...pt] };
        }
        // return { ...data, pricing_model_type: 'UNIFIED_PRICING_7' };
        return data;
      },
    }),

    verifyCoupon: builder.mutation<TCouponModelData, { couponStr: string; leadId: string }>({
      query: ({ couponStr, leadId }) => ({
        url: `verify-coupon/${leadId}`,
        method: 'POST',
        body: {
          coupon_codes: couponStr,
        },
      }),
    }),

    createProfile: builder.mutation<{ climb_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `profiles/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    createEnrollments: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `enrollments/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    createCCS: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `paymentmethods/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    createInvoices: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `invoices/first/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    discountCheckout: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `discount-checkout/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    createQuote: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `quotes/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    createRevisedQuote: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `revised-quotes/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    billDownPayment: builder.mutation<{ portal_url: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `billing/downpayment/${leadId}`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    raceCondition: builder.query<{ stable: boolean }, { leadId: string }>({
      query: ({ leadId }) => `/check/race-condition/?lead_id=${leadId}`,
      providesTags: ['UNIFIED_RACE_CONDITION'],
    }),

    uploadPdf: builder.mutation<
      { link: string },
      { leadId: string; isBridgePlan?: boolean; isReviseMode?: boolean; formData: FormData; account_charge?: string }
    >({
      query: ({ leadId, formData, isBridgePlan, isReviseMode, account_charge }) => {
        let updateStr = '&revised=true';
        if (account_charge) {
          updateStr = `${updateStr}&account_charge=${account_charge}`;
        }

        return {
          url: `pdf_upload/?lead_id=${leadId}${isBridgePlan ? '&bridge_plan_pdf_link=true' : ''}${
            isReviseMode ? updateStr : ''
          }`,
          method: 'POST',
          body: formData,
        };
      },
    }),

    acceptTermsConditions: builder.mutation<{ message: string }, { body: TCheckoutAPIDataModel; leadId: string }>({
      query: ({ leadId, body }) => ({
        url: `trigger-unified-email/${leadId}`,
        method: 'POST',
        body,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetMetadataQuery,
  useVerifyCouponMutation,
  useCreateProfileMutation,
  useCreateEnrollmentsMutation,
  useCreateCCSMutation,
  useCreateInvoicesMutation,
  useBillDownPaymentMutation,
  useCreateQuoteMutation,
  useCreateRevisedQuoteMutation,
  useRaceConditionQuery,
  useUploadPdfMutation,
  useDiscountCheckoutMutation,
  useAcceptTermsConditionsMutation,
} = extendedCheckApis;
